import React, { useState, useEffect, useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import PaginationTable from "../../../pagination/Pagination4";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { nodeName } from "jquery";

const EKYCCardsUnitsRC = ({
  distMonth,
  distYear,
  distMonthName,
  distName,
  afsoName,
  fpsId,
  ekycGeneratedDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemeData, setSchemeData] = useState([]);
  const [rcData, setRCData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");

  const formattedDate = useMemo(() => {
    if (!ekycGeneratedDate) return "";
    const [year, month, day] = ekycGeneratedDate.split("-");
    return `${day}-${month}-${year}`;
  }, [ekycGeneratedDate]);

  const getSchemeTotals = (schemeId, fieldPrefix) =>
    filteredData.reduce(
      (sum, item) => sum + Number(item[`${fieldPrefix}_${schemeId}`] || 0),
      0
    );

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [schemeRes, rcRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-card-units-scheme?distMonth=${distMonth}&distYear=${distYear}`
        ),
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-rc-card-units?distMonth=${distMonth}&distYear=${distYear}&fpsId=${fpsId}&ekycGeneratedDate=${formattedDate}`
        ),
      ]);

      if (!schemeRes.ok) throw new Error("Failed to fetch Scheme Details");
      if (!rcRes.ok) throw new Error("Failed to fetch RC Details");

      setSchemeData(await schemeRes.json());
      setRCData(await rcRes.json());
    } catch (err) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // inside component
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0); // reset to first page on search
  };

  useEffect(() => {
    if (formattedDate) fetchData();
  }, [distMonth, distYear, fpsId, formattedDate]);

  const filteredData = rcData.filter(
    (item) =>
      !searchQuery ||
      Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("EKYC Report");

    const totalColumns = 2 + schemeData.length * 8;

    // Title row
    const titleRow = worksheet.addRow([
      `EKYC Card Unit Status in District: ${distName}, Mandle: ${afsoName}, FPS Id: ${fpsId} for ${distMonthName} ${distYear}, Date: ${
        ekycGeneratedDate
          ? new Date(ekycGeneratedDate).toLocaleDateString("en-GB")
          : ""
      }`,
    ]);
    worksheet.mergeCells(1, 1, 1, totalColumns);
    titleRow.eachCell((cell) => {
      cell.font = { bold: true, size: 14 };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    // Header Row 1
    let headerRow1 = ["S.No", "RC Id"];
    schemeData.forEach((scheme) => {
      headerRow1.push(scheme.schemename); // one name per scheme (will merge 8 columns)
      for (let i = 1; i < 8; i++) headerRow1.push(""); // empty for merge
    });
    const row1 = worksheet.addRow(headerRow1);

    // Header Row 2
    const headerRow2 = ["", ""];
    schemeData.forEach(() => {
      headerRow2.push(
        "Total Units",
        "Fully e-kyc Done RC",
        "Fully e-kyc Done Units",
        "Partial e-kyc Done RC",
        "Partial e-kyc Done Units",
        "Partial e-kyc Not Done Units",
        "e-kyc Not Done RC",
        "e-kyc Not Done Units"
      );
    });
    const row2 = worksheet.addRow(headerRow2);

    // Merge cells
    worksheet.mergeCells(2, 1, 3, 1); // S.No
    worksheet.mergeCells(2, 2, 3, 2); // RC Id

    let colStart = 3;
    schemeData.forEach(() => {
      worksheet.mergeCells(2, colStart, 2, colStart + 7); // merge 8 columns per scheme
      colStart += 8;
    });

    [row1, row2].forEach((row) => {
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Add data rows
    rcData.forEach((item, index) => {
      const rowData = [index + 1, item.rc_id];
      schemeData.forEach((scheme) => {
        rowData.push(
          item[`total_units_${scheme.schemeid}`],
          item[`fully_ekyc_completed_rc_${scheme.schemeid}`],
          item[`fully_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_done_rc_${scheme.schemeid}`],
          item[`partial_ekyc_done_units_${scheme.schemeid}`],
          item[`partial_ekyc_not_done_units_${scheme.schemeid}`],
          item[`ekyc_not_done_rc_${scheme.schemeid}`],
          item[`ekyc_not_done_units_${scheme.schemeid}`]
        );
      });
      worksheet.addRow(rowData);
    });

    // TOTAL row
    const totalRowData = ["", "TOTAL"];
    schemeData.forEach((scheme) => {
      totalRowData.push(
        getSchemeTotals(scheme.schemeid, "total_units"),
        getSchemeTotals(scheme.schemeid, "fully_ekyc_completed_rc"),
        getSchemeTotals(scheme.schemeid, "fully_ekyc_done_units"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_done_rc"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_done_units"),
        getSchemeTotals(scheme.schemeid, "partial_ekyc_not_done_units"),
        getSchemeTotals(scheme.schemeid, "ekyc_not_done_rc"),
        getSchemeTotals(scheme.schemeid, "ekyc_not_done_units")
      );
    });
    const totalRow = worksheet.addRow(totalRowData);
    totalRow.font = { bold: true };

    worksheet.columns.forEach((col) => (col.width = 18));
    worksheet.views = [{ state: "frozen", ySplit: 3 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `EKYC_RC_Report_${distMonthName}_${distYear}.xlsx`);
  };

  return (
    <>
      {schemeData.length > 0 ? (
        <div
          style={{
            margin: "0 auto 70px auto",
            width: "100%",
            paddingLeft: "20px",
            paddingRight: "20px",
            overflowX: "hidden",
          }}
        >
          {/* Pagination + Search */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            {/* Pagination centered */}
            <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <PaginationTable
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(value) =>
                  setRowsPerPage(
                    value === "all" ? rcData.length : parseInt(value, 10)
                  )
                }
              />
            </div>

            {/* Search on right */}
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={handleSearchChange}
              style={{ padding: "8px", width: "200px" }}
            />
          </div>

          {/* Table */}
          <div style={{ overflowX: "auto", border: "1px" }}>
            <table style={{  width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ position: "sticky", top: 0 }}>
                <tr className="tableheader">
                  <th
                    colSpan={2 + schemeData.length * 9}
                    style={{ textAlign: "center", padding: "10px" }}
                  >
                    EKYC Card Unit Status in District : {distName}, Mandle:{" "}
                    {afsoName}, FPS Id: {fpsId} for {distMonthName} {distYear},
                    Date:{" "}
                    {formattedDate
                      ? new Date(formattedDate).toLocaleDateString("en-GB")
                      : ""}
                  </th>
                </tr>
                <tr className="tableheader">
                  <th rowSpan={2}>S.No</th>
                  <th rowSpan={2}>RC Id</th>
                  {schemeData.map((scheme) => (
                    <th key={scheme.schemeid} colSpan={8}>
                      {scheme.schemename}
                    </th>
                  ))}
                </tr>
                <tr className="tableheader">
                  {schemeData.map((scheme) => (
                    <React.Fragment key={scheme.schemeid}>
                      <th>Total Units</th>
                      <th>Fully e-kyc Done RC</th>
                      <th>Fully e-kyc Done Units</th>
                      <th>Partial e-kyc Done RC</th>
                      <th>Partial e-kyc Done Units</th>
                      <th>Partial e-kyc Not Done Units</th>
                      <th>e-kyc Not Done RC</th>
                      <th>e-kyc Not Done Units</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: "center" }}>
                      {index + 1 + page * rowsPerPage}
                    </td>
                    <td>{item.rc_id}</td>
                    {schemeData.map((scheme) => (
                      <React.Fragment key={scheme.schemeid}>
                        {[
                          "total_units",
                          "fully_ekyc_completed_rc",
                          "fully_ekyc_done_units",
                          "partial_ekyc_done_rc",
                          "partial_ekyc_done_units",
                          "partial_ekyc_not_done_units",
                          "ekyc_not_done_rc",
                          "ekyc_not_done_units",
                        ].map((field) => (
                          <td key={field} style={{ textAlign: "right" }}>
                            {item[`${field}_${scheme.schemeid}`]}
                          </td>
                        ))}
                      </React.Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="tableheader">
                  <td colSpan={2} style={{ textAlign: "center" }}>
                    TOTAL
                  </td>
                  {schemeData.map((scheme) =>
                    [
                      "total_units",
                      "fully_ekyc_completed_rc",
                      "fully_ekyc_done_units",
                      "partial_ekyc_done_rc",
                      "partial_ekyc_done_units",
                      "partial_ekyc_not_done_units",
                      "ekyc_not_done_rc",
                      "ekyc_not_done_units",
                    ].map((field) => (
                      <td
                        key={`${scheme.schemeid}-${field}`}
                        style={{ textAlign: "right" }}
                      >
                        {getSchemeTotals(scheme.schemeid, field)}
                      </td>
                    ))
                  )}
                </tr>
              </tfoot>
            </table>

            <div style={{ marginTop: "20px", textAlign: "center" }}>
              {
                <button
                  onClick={() => handleDownloadExcel()}
                  className="button button2"
                >
                  Download Excel
                </button>
              }
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          No data found
        </div>
      )}
      <br />
    </>
  );
};

export default EKYCCardsUnitsRC;
